/** Approach B: 3-toggle view over run_until + ecm_write_mode. */

export type FormFlag = "process" | "populate" | "complete";

export interface FormToggleFlags {
    processEnabled: boolean;
    populateEnabled: boolean;
    completeEnabled: boolean;
}

export const DEFINING_FLAGS_TITLE =
    "This form is still defining. Freeze it before turning on Process this form, Send verdict back, or Complete on verdict.";
export const PROCESS_OFF_TITLE = "Turn on Process this form first.";
export const SEND_VERDICT_OFF_TITLE = "Turn on Send verdict back first.";

/**
 * Same AND chain as the Enabled-forms toggles: defining locks all three;
 * Populate requires Process; Complete requires Process AND Populate.
 */
export const isFormFlagDisabled = (
    state: string,
    flags: FormToggleFlags,
    column: FormFlag
): boolean => {
    if (state === "defining") {
        return true;
    }
    if (column === "process") {
        return false;
    }
    if (!flags.processEnabled) {
        return true;
    }
    if (column === "complete") {
        return !flags.populateEnabled;
    }
    return false;
};

/** Native `title` on disabled toggles — Account admin has no Skylab tooltip on these. */
export const disabledFlagTitle = (
    state: string,
    flags: FormToggleFlags,
    column: FormFlag
): string | undefined => {
    if (state === "defining") {
        return DEFINING_FLAGS_TITLE;
    }
    if (column === "process") {
        return undefined;
    }
    if (!flags.processEnabled) {
        return PROCESS_OFF_TITLE;
    }
    if (column === "complete" && !flags.populateEnabled) {
        return SEND_VERDICT_OFF_TITLE;
    }
    return undefined;
};

export interface FormModePayload {
    runUntil: string;
    ecmWriteMode: string | null;
}

export const flagsFromMode = (
    runUntil: string | null | undefined,
    ecmWriteMode: string | null | undefined
): FormToggleFlags => {
    const until = runUntil || "off";
    if (until === "off") {
        return { processEnabled: false, populateEnabled: false, completeEnabled: false };
    }
    if (until === "ecm_sync") {
        return {
            processEnabled: true,
            populateEnabled: true,
            completeEnabled: ecmWriteMode === "complete",
        };
    }
    return { processEnabled: true, populateEnabled: false, completeEnabled: false };
};

export const modeFromFlags = (flags: FormToggleFlags): FormModePayload => {
    if (!flags.processEnabled) {
        return { runUntil: "off", ecmWriteMode: null };
    }
    if (!flags.populateEnabled) {
        return { runUntil: "validation", ecmWriteMode: null };
    }
    if (flags.completeEnabled) {
        return { runUntil: "ecm_sync", ecmWriteMode: "complete" };
    }
    return { runUntil: "ecm_sync", ecmWriteMode: "pending" };
};

/** Defining rows are catalog-only: all three toggles render off and stay locked. */
export const visibleFlags = (state: string, flags: FormToggleFlags): FormToggleFlags =>
    state === "defining"
        ? { processEnabled: false, populateEnabled: false, completeEnabled: false }
        : flags;

export const nextFlags = (
    current: FormToggleFlags,
    flag: FormFlag,
    enabled: boolean
): FormToggleFlags => {
    if (flag === "process") {
        if (!enabled) {
            return { processEnabled: false, populateEnabled: false, completeEnabled: false };
        }
        return { ...current, processEnabled: true };
    }
    if (flag === "populate") {
        if (!enabled) {
            return { ...current, processEnabled: true, populateEnabled: false, completeEnabled: false };
        }
        return { ...current, processEnabled: true, populateEnabled: true };
    }
    if (!enabled) {
        return { ...current, processEnabled: true, populateEnabled: true, completeEnabled: false };
    }
    return { processEnabled: true, populateEnabled: true, completeEnabled: true };
};
